 'use client'

import { useTranslations } from 'next-intl'
import { useAuth } from '@/lib/stores/auth-store'
import { cn } from '@/lib/utils'
import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Clock,
  FileText,
  FolderOpen,
  Home,
  Newspaper,
  Settings,
  UserCog,
  Bug,
  Layout,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Target,
  Calendar,
  MessageSquare,
  StickyNote,
  Trophy,
  BarChart3,
  CheckSquare,
  BarChart2,
  Zap
} from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { getAuthHeaders } from '@/lib/api-utils'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { DbSprint } from '@/lib/types/db-sprint'

interface SidebarProps {
  collapsed: boolean
  mobileOpen?: boolean
  onCloseMobile?: () => void
}

interface Version {
  id: number
  name: string
  status: string
  due_date?: string
  project: {
    id: number
    name: string
  }
}

// Using DbSprint type from database layer
type Sprint = DbSprint

// Navigation organized by category
const navigationGroups = [
  {
    title: 'Core',
    items: [
      { name: 'Home', href: '/', icon: Home },
      { name: 'My page', href: '/my', icon: FileText },
      { name: 'Todo', href: '/todo', icon: CheckSquare },
    ]
  },
  {
    title: 'Work Management',
    items: [
      { name: 'Projects', href: '/projects', icon: FolderOpen },
      { name: 'Issues', href: '/issues', icon: Bug },
      { name: 'Sprints', href: '/sprints', icon: Zap },
      // Kanban is now accessible via Sprints page (click on sprint row)
      // { name: 'Kanban', href: '/kanban', icon: Layout, hasSubmenu: true },
      { name: 'Gantt', href: '/gantt', icon: BarChart3, hasSubmenu: true },
      { name: 'Roadmap', href: '/roadmap', icon: BarChart2 },
    ]
  },
  {
    title: 'Collaboration',
    items: [
      { name: 'News', href: '/news', icon: Newspaper },
      { name: 'Notes', href: '/notes', icon: StickyNote },
      { name: 'Lounge', href: '/lounge', icon: MessageSquare },
    ]
  },
  {
    title: 'Tracking',
    items: [
      { name: 'Time Tracking', href: '/time', icon: Clock },
      { name: 'Calendar', href: '/calendar', icon: Calendar },
    ]
  },
  {
    title: 'Engagement',
    items: [
      { name: 'Gamification', href: '/gamification', icon: Trophy },
    ]
  }
]

// Flat array for compatibility with existing code
const navigation = navigationGroups.flatMap(group => group.items)

export function Sidebar({ collapsed, mobileOpen = false, onCloseMobile }: SidebarProps) {
  let t: any
  try {
    t = useTranslations()
  } catch (error) {
    console.error('❌ useTranslations failed:', error)
    // Provide a fallback translation function
    t = (key: string) => key.split('.').pop() || key
  }
  
  const { user } = useAuth()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  // Admin routes temporarily disabled; keep sidebar generic for now
  const isAdmin = false

  // Safe translation helper with fallback
  const translate = (key: string, fallback?: string) => {
    try {
      const translated = t(key)
      // If translation returns the key itself (not found), use fallback or extract text from key
      if (translated === key && fallback) {
        return fallback
      }
      return translated || fallback || key.split('.').pop() || key
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error)
      return fallback || key.split('.').pop() || key
    }
  }

  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [versions, setVersions] = useState<Version[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')


  // Auto-expand Kanban submenu when on Kanban page
  useEffect(() => {
    if (pathname === '/kanban') {
      setExpandedMenus(new Set(['Kanban']))
    } else if (pathname === '/gantt') {
      setExpandedMenus(new Set(['Gantt']))
    } else if (pathname === '/sprints') {
      setExpandedMenus(new Set(['Sprints']))
    }
  }, [pathname])

  // Memoize project ID to prevent unnecessary dependency changes
  const currentProjectId = useMemo(() => {
    // First check URL query parameter (for Kanban, Gantt pages)
    const urlProject = searchParams.get('project')
    if (urlProject) return urlProject
    
    // For sprint detail pages: /projects/[id]/sprints/[sprintId]
    if (pathname.startsWith('/projects/')) {
      const pathParts = pathname.split('/')
      if (pathParts[1] === 'projects' && pathParts[2]) {
        return pathParts[2] // Extract project ID from path
      }
    }

    return null
  }, [searchParams, pathname])

  // Memoize fetch function to prevent recreating on every render
  const fetchVersions = useCallback(async (projectId: string) => {
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch(`/api/projects/${projectId}/versions`, {
        headers: authHeaders
      })
      if (response.ok) {
        const data = await response.json()
        setVersions(data.versions || [])
      }
    } catch (err) {
    }
  }, [])


  // Fetch versions ONLY when on Kanban/Gantt pages AND project changes
  useEffect(() => {
    // Only fetch versions if we're on Kanban or Gantt page
    if (pathname !== '/kanban' && pathname !== '/gantt') {
      // Clear versions if not on these pages
      if (versions.length > 0) {
        setVersions([])
        setSelectedProjectId('')
      }
      return
    }

    if (currentProjectId && currentProjectId !== selectedProjectId) {
      setSelectedProjectId(currentProjectId)
      fetchVersions(currentProjectId)
    } else if (!currentProjectId && selectedProjectId) {
      // Clear versions if no project selected
      setVersions([])
      setSelectedProjectId('')
    }
  }, [pathname, currentProjectId, selectedProjectId, versions.length, fetchVersions])


  const toggleMenu = (menuName: string) => {
    const newExpanded = new Set(expandedMenus)
    if (newExpanded.has(menuName)) {
      newExpanded.delete(menuName)
    } else {
      newExpanded.add(menuName)
    }
    setExpandedMenus(newExpanded)
  }

  const toggleGroup = (groupTitle: string) => {
    const newCollapsed = new Set(collapsedGroups)
    if (newCollapsed.has(groupTitle)) {
      newCollapsed.delete(groupTitle)
    } else {
      newCollapsed.add(groupTitle)
    }
    setCollapsedGroups(newCollapsed)
  }

  const handleVersionClick = (versionId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    // Direct navigation without transition wrapper for faster response
    if (pathname === '/gantt') {
      router.push(`/gantt?project=${selectedProjectId}&version=${versionId}`)
    } else {
      router.push(`/kanban?project=${selectedProjectId}&version=${versionId}`)
    }
    if (onCloseMobile) {
      onCloseMobile()
    }
  }


  // Build the aside content first
  const asideContent = (
    <aside
      className={cn(
        // Fixed positioning, below header
        "fixed top-16 h-[calc(100vh-4rem)] bg-background overflow-y-auto scrollbar-hide",
        "transition-transform duration-200 ease-in-out",
        // RTL/LTR positioning
        "ltr:left-0 ltr:border-r rtl:right-0 rtl:border-l border-border",
        // Mobile: hidden by default, visible when mobileOpen is true
        // Desktop: always visible
        mobileOpen 
          ? 'translate-x-0' 
          : 'ltr:-translate-x-full rtl:translate-x-full md:ltr:translate-x-0 md:rtl:translate-x-0',
        // Width
        collapsed ? "w-full md:w-16" : "w-full md:w-64",
        // z-index below modals but above content
        "z-40"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <nav className="p-2">
        <ul className="space-y-0">
          {navigationGroups.map((group, groupIndex) => {
            const isGroupCollapsed = collapsedGroups.has(group.title)

            return (
              <li key={group.title}>
                {/* Divider before each group (except first) */}
                {groupIndex > 0 && !collapsed && (
                  <div className="my-3 px-3">
                    <div className="h-px bg-border" />
                  </div>
                )}

                {/* Collapsible Group Header */}
                {!collapsed && (
                  <button
                    onClick={() => toggleGroup(group.title)}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 group mb-1"
                  >
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
                      {translate(`sidebar.${group.title.toLowerCase().replace(/ /g, '')}`, group.title)}
                    </h3>
                    <div className={cn(
                      "transition-transform duration-200",
                      isGroupCollapsed ? "rotate-0" : "rotate-0"
                    )}>
                      {isGroupCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      )}
                    </div>
                  </button>
                )}

                {/* Group Items - Hidden when group is collapsed (but always show when sidebar is collapsed/icon-only) */}
                {(collapsed || !isGroupCollapsed) && (
                  <ul className={cn(
                    "space-y-0",
                    !collapsed && "px-1 pb-2"
                  )}>
                    {group.items.map((item) => {
                      const Icon = item.icon
                      // Check if current page matches this menu item
                      let isActive = pathname === item.href
                      // Special case: Sprints menu is active on sprint detail pages too
                      if (item.name === 'Sprints' && (pathname.startsWith('/sprints') || (pathname.startsWith('/projects/') && pathname.includes('/sprints/')))) {
                        isActive = true
                      }
                      const isExpanded = expandedMenus.has(item.name)

                  return (
                    <li key={item.name} className="relative">
                {item.hasSubmenu && !collapsed ? (
                  <div className="relative -ml-2">
                    {/* Continuous border for parent and children */}
                    {isActive && (
                      <div className={cn(
                        "absolute ltr:left-0 rtl:right-0 top-0 w-1 bg-primary transition-all duration-300 ease-out",
                        isExpanded ? "h-full ltr:rounded-r-lg rtl:rounded-l-lg" : "h-full ltr:rounded-r-full rtl:rounded-l-full"
                      )} />
                    )}
                    
                    {isActive ? (
                      <div
                        onClick={() => toggleMenu(item.name)}
                        className={cn(
                          "relative flex items-center rounded-lg transition-all duration-200 ease-in-out group cursor-pointer",
                          "ps-6 pe-4 py-2",
                          "text-primary"
                        )}
                      >
                        <Icon className={cn("transition-all duration-200 h-5 w-5 me-3 text-primary")} />

                        <span className={cn("truncate font-medium transition-all duration-200 flex-1 font-semibold text-primary")}>{translate(`sidebar.${item.name.toLowerCase().replace(/ /g, '')}`, item.name)}</span>

                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 ms-auto text-primary" />
                        ) : (
                          <ChevronRight className="h-5 w-5 ms-auto text-primary" />
                        )}
                      </div>
                    ) : (
                      <Link href={item.href} prefetch={false}>
                        <div 
                          className={cn(
                            "relative flex items-center rounded-lg transition-all duration-200 ease-in-out group cursor-pointer",
                            "ps-6 pe-4 py-2",
                            "text-muted-foreground hover:text-foreground"
                          )}
                          onClick={onCloseMobile}
                        >
                          <Icon className={cn("transition-all duration-200 h-5 w-5 me-3")} />
                          <span className={cn("truncate font-medium transition-all duration-200 flex-1")}>{translate(`sidebar.${item.name.toLowerCase().replace(/ /g, '')}`, item.name)}</span>
                        </div>
                      </Link>
                    )}

                    {isExpanded && item.name === 'Kanban' && pathname === '/kanban' && (
                      <ul className="ps-0 ms-0 space-y-px">
                        {versions.length > 0 ? (
                          versions.map((version, index) => {
                            const isSelected = searchParams.get('version') === version.id.toString()
                            const isClosed = version.status === 'closed'

                            return (
                              <li key={version.id}>
                                {index > 0 && <div className="border-t border-border/40 mx-6" />}
                                <button 
                                  onClick={(e) => handleVersionClick(version.id.toString(), e)} 
                                  className={cn(
                                    "w-full text-left ps-6 pe-4 py-2 rounded-md transition-colors",
                                    "flex items-center gap-3 font-medium text-[13px]", 
                                    isSelected 
                                      ? "font-semibold text-foreground" 
                                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                  )}
                                >
                                  {isClosed ? (
                                    <CheckCircle2 className={cn(
                                      "h-5 w-5 flex-shrink-0",
                                      isSelected ? "text-primary" : "text-muted-foreground"
                                    )} />
                                  ) : (
                                    <Target className={cn(
                                      "h-5 w-5 flex-shrink-0",
                                      isSelected ? "text-primary" : "text-muted-foreground"
                                    )} />
                                  )}
                                  <span className="flex-1 truncate">{version.name}</span>
                                </button>
                              </li>
                            )
                          })
                        ) : (
                          <li className="ps-6 pe-4 py-2">
                            <div className="text-xs text-muted-foreground italic">No versions available</div>
                          </li>
                        )}
                      </ul>
                    )}
                    {isExpanded && item.name === 'Gantt' && pathname === '/gantt' && (
                      <ul className="ps-0 ms-0 space-y-px">
                        {versions.length > 0 ? (
                          versions.map((version, index) => {
                            const isSelected = searchParams.get('version') === version.id.toString()
                            const isClosed = version.status === 'closed'

                            return (
                              <li key={version.id}>
                                {index > 0 && <div className="border-t border-border/40 mx-6" />}
                                <button 
                                  onClick={(e) => handleVersionClick(version.id.toString(), e)} 
                                  className={cn(
                                    "w-full text-left ps-6 pe-4 py-2 rounded-md transition-colors",
                                    "flex items-center gap-3 font-medium text-[13px]",
                                    isSelected 
                                      ? "font-semibold text-foreground" 
                                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                  )}
                                >
                                  {isClosed ? (
                                    <CheckCircle2 className={cn(
                                      "h-5 w-5 flex-shrink-0",
                                      isSelected ? "text-primary" : "text-muted-foreground"
                                    )} />
                                  ) : (
                                    <Target className={cn(
                                      "h-5 w-5 flex-shrink-0",
                                      isSelected ? "text-primary" : "text-muted-foreground"
                                    )} />
                                  )}
                                  <span className="flex-1 truncate">{version.name}</span>
                                </button>
                              </li>
                            )
                          })
                        ) : (
                          <li className="ps-6 pe-4 py-2">
                            <div className="text-xs text-muted-foreground italic">No versions available</div>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link href={item.href} prefetch={true}>
                    <div 
                      className={cn(
                        "relative flex items-center rounded-lg transition-all duration-200 ease-in-out group", 
                        collapsed ? "justify-center px-3 py-3" : "justify-start px-4 py-1.5", 
                        isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={onCloseMobile}
                    >
                      {isActive && <div className="absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary ltr:rounded-r-full rtl:rounded-l-full transition-all duration-300 ease-out" />}

                      <Icon className={cn("transition-all duration-200", collapsed ? "h-6 w-6" : "h-5 w-5 me-4", isActive && "scale-110")} />

                      {!collapsed && <span className={cn("truncate font-medium transition-all duration-200", isActive && "font-semibold")}>{translate(`sidebar.${item.name.toLowerCase().replace(/ /g, '')}`, item.name)}</span>}
                    </div>
                  </Link>
                )}
                    </li>
                      )
                    })}
                  </ul>
                )}
              </li>
            )
          })}

          {/* Settings Section */}
          <li>
            {/* Divider before Settings */}
            {!collapsed && (
              <div className="my-3 px-3">
                <div className="h-px bg-border" />
              </div>
            )}

            {!collapsed && (
              <div className="px-4 py-2.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('common.settings')}</h3>
              </div>
            )}
            <ul className="space-y-0">
              <li className="relative">
                <Link href="/settings" prefetch={false}>
                  <div 
                    className={cn(
                      "relative flex items-center rounded-lg transition-all duration-200 ease-in-out group", 
                      collapsed ? "justify-center px-3 py-3" : "justify-start px-4 py-1.5", 
                      pathname === "/settings" ? "text-primary" : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={onCloseMobile}
                  >
                    {pathname === "/settings" && <div className="absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary ltr:rounded-r-full rtl:rounded-l-full transition-all duration-300 ease-out" />}
                    <Settings className={cn("transition-all duration-200", collapsed ? "h-6 w-6" : "h-5 w-5 me-4", pathname === "/settings" && "scale-110")} />
                    {!collapsed && <span className={cn("truncate font-medium transition-all duration-200", pathname === "/settings" && "font-semibold")}>{t('common.settings')}</span>}
                  </div>
                </Link>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </aside>
  )

  return asideContent
}